import { css, cx } from '@linaria/core'
import { Fragment, type HTMLProps, type ReactNode, useId, useMemo, useState } from 'react'
import { fragments, style } from '#styles'
import { HoverTooltip } from './HoverTooltip'

const segmentedButtonClass = css`
  display: grid;
  grid-auto-columns: minmax(min-content, 1fr);
  grid-auto-flow: column;
  
  align-items: stretch;
  gap: 1px;
  width: max-content;
  max-width: 100%;
  border: 1px solid ${style.themeColors.lineDefault};
  background-color: ${style.themeColors.lineDefault};
  
  &._kind-m {
    min-height: 28px;
  }

  &._kind-l {
    min-height: 44px;
  }

  > ._button {
    flex: 1;
    cursor: pointer;
    padding: 0 24px;
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: center;
    line-height: 100%;
    color: ${style.themeColors.textDefault};
    background-color: ${style.themeColors.backgroundBoxed};
    border: none;
    transition: 0.25s ease color, 0.25s ease all;
    ${fragments.textStyle.button.m};

    &:hover {
      color: ${style.themeColors.textLogo}
      transition: 0.25s ease color, 0.25s ease all;
      background-color: ${style.themeColors.backgroundBoxedHover};
    }

    &:focus-visible {
      outline: 2px solid ${style.themeColors.lineFocus};
      outline-offset: -1px;
    }
    
    &._selected {
      background: ${style.themeColors.backgroundDefault};
    }
    

    &._disabled {
      cursor: not-allowed;
      color: ${style.themeColors.textDimmed};

      &:hover {
        color: ${style.themeColors.textDimmed}
      }
    }
  }

  &._slim {
    > ._button {
      padding: 0 8px;
    }
  }
  
  &._kind-grid {
    display: grid;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
    gap: 1px;
    min-height: 44px;
  }
`

// base option type
export type SegmentedButtonOption<Id extends string = string> = {
  id: Id
  content: ReactNode
  title?: string
  disabled?: boolean
}

// generic in place to transport brands and such
export interface SegmentedButtonBaseProps<Id extends string = string> {
  options: SegmentedButtonOption<Id>[]
  kind?: 'm' | 'l' | 'grid'
  multiple?: boolean
  slim?: boolean
}

interface SegmentedButtonSingleProps<Id extends string = string> extends SegmentedButtonBaseProps<Id> {
  onSelectionChange?: (selection: Id) => unknown
  initialSelection?: Id
  selection?: Id
  multiple?: false
}

interface SegmentedButtonMultiProps<Id extends string = string> extends SegmentedButtonBaseProps<Id> {
  onSelectionChange?: (selections: Id[]) => unknown
  initialSelection?: Id[]
  selection?: Id[]
  multiple: true
}

export type SegmentedButtonProps<Id extends string = string> = SegmentedButtonSingleProps<Id> | SegmentedButtonMultiProps<Id>

export const SegmentedButton = <Id extends string = string>({
  options,
  onSelectionChange,
  initialSelection,
  selection,
  multiple,
  kind = 'm',
  slim,
  className,
  ...divProps
}: SegmentedButtonProps<Id> & HTMLProps<HTMLDivElement>): ReactNode => {
  const [localMultiSelection, setLocalMultiSelection] = useState<Id[]>(initialSelection ? (multiple ? initialSelection : [initialSelection]) : [])
  const arraySelection = useMemo(() => (selection ? (multiple ? selection : [selection]) : undefined), [selection, multiple])
  const multiSelection = arraySelection ?? localMultiSelection
  const id = useId()

  return (
    <div id={id} className={cx(segmentedButtonClass, `_kind-${kind}`, slim && '_slim', className)} {...divProps}>
      {options.map((x) => {
        const selected = multiSelection.includes(x.id)
        const props = {
          type: 'button',
          title: x.title,
          className: cx('_button', selected && '_selected', x.disabled && '_disabled'),
          onClick: () => {
            if (x.disabled) {
              return
            }
            // discern between the multi and single handling, depending on that, only one selection or all selections are allowed
            if (multiple) {
              const result = selected ? multiSelection.filter((y) => y !== x.id) : [...multiSelection, x.id]
              setLocalMultiSelection(result)
              onSelectionChange?.(result)
            } else {
              setLocalMultiSelection([x.id])
              onSelectionChange?.(x.id)
            }
          },
        } as const

        return (
          <Fragment key={x.id}>
            {x.title ? <HoverTooltip.button {...props}>{x.content}</HoverTooltip.button> : <button {...props}>{x.content}</button>}
          </Fragment>
        )
      })}
    </div>
  )
}
